import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  allChangeRequests,
  allChangeRequestsIsMobile,
} from "my-hr/configs/my-hr-table-defs";
import { OcCard, OcDataTable, OcRoleFilter } from "oc/components";
import { ProductsT, RoleCategoriesT } from "oc/configs/constants";
import { useOcButtonGroup } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";

type MyHrAllChangeRequestsCardProps = {
  allUserCount: number;
  buttonDisable: boolean;
  contract: any;
  data: any[];
  handleMoreButton: (row: any) => void;
  loading: boolean;
  onAddFilter: (filter: any) => void;
  onClearFilters: () => void;
  onLoadQuery: () => void;
  onOnlyRequestedChange: (onlyRequested: boolean) => void;
  onRemoveFilter: (filter: any) => void;
  onSelectRow: (row: any) => void;
  onTableDataChange: (data: any) => void;
  product?: string;
  requestsCount: number;
  role?: string;
  roleFilters: any;
  selectedRowId: string | number | null;
  tableData: any;
  tableId: string;
};

export const MyHrAllChangeRequestsCard = ({
  allUserCount,
  buttonDisable,

  data,
  handleMoreButton,
  loading,
  onAddFilter,
  onClearFilters,
  onLoadQuery,
  onOnlyRequestedChange,
  onRemoveFilter,
  onSelectRow,
  onTableDataChange,
  product = "MyHR",
  requestsCount,
  role = "examiner",
  roleFilters,
  selectedRowId,
  tableData,
  tableId,
}: MyHrAllChangeRequestsCardProps) => {
  const { t } = useTranslation();
  const isMobile = useOcIsMobile();

  const tableDef = isMobile ? allChangeRequestsIsMobile : allChangeRequests;

  const filteredTableDef = {
    id: allChangeRequests?.id,
    columns: allChangeRequests?.columns?.filter(
      (column) =>
        !allChangeRequestsIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  const buttonsDef = ["OC_TO_BE_ASSESSED", "OC_ALL"];
  const { OcButtonGroup, selectedButton } = useOcButtonGroup({
    id: "AllChangeRequestsCard",
    buttonsDef,
  });

  useEffect(() => {
    if (selectedButton === "OC_TO_BE_ASSESSED") {
      onOnlyRequestedChange(true);
    } else if (selectedButton === "OC_ALL") {
      onOnlyRequestedChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButton]);

  const list = {
    rows: data,
    total: data?.length,
  };

  return (
    <OcCard
      label={
        tableId === "allChangeRequestsTableData"
          ? t("MY_HR_CHANGE_REQUESTS")
          : t("MY_LEAVE_LEAVE_AND_ABSENCE_REQUESTS")
      }
    >
      <>
        <OcRoleFilter
          subtypeProps={{
            queryType: "notAdmin",
            roleFilters: roleFilters,
            handleAddItem: onAddFilter,
            handleRemoveItem: onRemoveFilter,
            product: product as ProductsT,
            role: role as RoleCategoriesT,
            isCheckbox: true,
          }}
          OcButtonGroup={OcButtonGroup}
          summaryItems={[
            {
              label: "OC_TOTAL_COUNT_OF_EMPLOYEES",
              value: allUserCount,
            },
            {
              label: "OC_COUNT_OF_REQUESTS",
              value: requestsCount,
              color: "primary",
            },
          ]}
          buttonBarProps={{
            handlePrimaryButton: onLoadQuery,
            primaryButtonDisabled: buttonDisable,
            handleRightButton: onClearFilters,
            noLine: true,
            paddingTop: "10px",
            rightButtonText: t("OC_RESET"),
            rightButtonId: "cancel-button",
            rightButtonColor: "secondary",
            primaryButtonText: t("OC_FILTERING"),
            primaryButtonId: "filter-button",
          }}
        />
        <Box paddingTop="32px">
          <OcDataTable
            data={list}
            loading={loading}
            tableDef={tableDef}
            tableData={tableData}
            setTableData={onTableDataChange}
            onSelectRow={onSelectRow}
            tableId={tableId}
            pagination={true}
            localPaging={true}
            filteredTableDef={filteredTableDef}
            selectedRowId={selectedRowId ?? undefined}
            handleMoreButton={handleMoreButton}
          />
        </Box>
      </>
    </OcCard>
  );
};

export default MyHrAllChangeRequestsCard;
